import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChatsCircle, Plus, Trash } from "@phosphor-icons/react";
import { api, lies } from "../../api/client";
import type { Anzeige, AnzeigeStatus, Leistung, Nachtraege, Nachtrag, NachtragStatus, Projekt } from "../../api/types";
import { Auswahl } from "../../components/Auswahl";
import { Dialog, Feld, Meldung } from "../../components/Bausteine";
import { Diktat } from "../../components/Diktat";
import { datum, euro, heute } from "../../lib/format";
import { useLeistungen } from "./MaengelReiter";

const ANZEIGE_ART = { behinderung: "Behinderungsanzeige", bedenken: "Bedenkenanzeige" } as const;
const ANZEIGE_GRUND = { behinderung: "§ 6 Abs. 1 VOB/B", bedenken: "§ 4 Abs. 3 VOB/B" } as const;
const ANZEIGE_STATUS: Record<AnzeigeStatus, string> = {
  offen: "offen", beantwortet: "beantwortet", erledigt: "erledigt", zurueckgewiesen: "zurückgewiesen",
};
const NACHTRAG_STATUS: Record<NachtragStatus, string> = {
  angekuendigt: "angekündigt", angeboten: "angeboten", geprueft: "geprüft", beauftragt: "beauftragt",
  abgelehnt: "abgelehnt", zurueckgezogen: "zurückgezogen",
};
const FARBE: Record<string, string> = {
  offen: "warn", beantwortet: "akzent", erledigt: "gut", zurueckgewiesen: "",
  angekuendigt: "warn", angeboten: "warn", geprueft: "akzent", beauftragt: "gut", abgelehnt: "", zurueckgezogen: "",
};

/** Behinderungs-/Bedenkenanzeigen und Nachträge (SPEC §13). */
export function AnzeigenReiter({ projekt, kann }: { projekt: Projekt; kann: boolean }) {
  const leistungen = useLeistungen(projekt.id).data ?? [];
  return (
    <>
      <Anzeigen projekt={projekt} kann={kann} leistungen={leistungen} />
      <NachtragListe projekt={projekt} kann={kann} leistungen={leistungen} />
    </>
  );
}

function LeistungWahl({ leistungen, wert, setze }: { leistungen: Leistung[]; wert: string; setze: (v: string) => void }) {
  return (
    <Auswahl value={wert} onChange={(e) => setze(e.target.value)}>
      <option value="">–</option>
      {leistungen.map((l) => <option key={l.id} value={l.id}>{l.bezeichnung}{l.an && ` – ${l.an}`}</option>)}
    </Auswahl>
  );
}

// ================================================================== Anzeigen

function Anzeigen({ projekt, kann, leistungen }: { projekt: Projekt; kann: boolean; leistungen: Leistung[] }) {
  const schluessel = ["anzeigen", projekt.id];
  const liste = useQuery({ queryKey: schluessel, queryFn: () => lies<Anzeige[]>(`/projekte/${projekt.id}/anzeigen`) });
  const [neu, setNeu] = useState<"behinderung" | "bedenken" | null>(null);
  const [offen, setOffen] = useState<Anzeige | null>(null);
  return (
    <section className="karte">
      <div className="karte-kopf">
        <h2>Behinderungs- und Bedenkenanzeigen</h2>
        <div className="luft" />
        {kann && <>
          <button className="knopf" onClick={() => setNeu("behinderung")}><Plus size={16} weight="bold" /> Behinderung</button>
          <button className="knopf" onClick={() => setNeu("bedenken")}><Plus size={16} weight="bold" /> Bedenken</button>
        </>}
      </div>
      <Meldung fehler={liste.error} />
      <div className="liste">
        {liste.data?.map((a) => (
          <button key={a.id} className="zeile zeile-knopf" onClick={() => setOffen(a)}>
            <span className="nr">{a.art === "behinderung" ? "BH" : "BD"}-{String(a.lfd).padStart(2, "0")}</span>
            <div className="haupt">
              <div>{a.gegenstand}</div>
              <div className="mute">
                {[ANZEIGE_ART[a.art], a.von, `eingegangen ${datum(a.eingang_am)}`, a.leistung].filter(Boolean).join(" · ")}
              </div>
            </div>
            <span style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
              {a.frist && a.status === "offen" && <span className="chip">Antwort bis {datum(a.frist)}</span>}
              {a.punkt_id && <span className="chip"><ChatsCircle size={13} /> im Protokoll</span>}
              <span className={`chip ${FARBE[a.status]}`}>{ANZEIGE_STATUS[a.status]}</span>
            </span>
          </button>
        ))}
        {liste.data?.length === 0 && <div className="leer">Keine Anzeigen eingegangen.</div>}
      </div>
      {neu && <AnzeigeDialog projekt={projekt} art={neu} leistungen={leistungen} kann zu={() => setNeu(null)} />}
      {offen && <AnzeigeDialog projekt={projekt} art={offen.art} anzeige={offen} leistungen={leistungen} kann={kann}
                               zu={() => setOffen(null)} />}
    </section>
  );
}

function AnzeigeDialog({ projekt, art, anzeige, leistungen, kann, zu }: {
  projekt: Projekt; art: "behinderung" | "bedenken"; anzeige?: Anzeige; leistungen: Leistung[]; kann: boolean; zu: () => void;
}) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    leistung_id: anzeige?.leistung_id ?? "", eingang_am: anzeige?.eingang_am ?? heute(), von: anzeige?.von ?? "",
    gegenstand: anzeige?.gegenstand ?? "", auswirkung: anzeige?.auswirkung ?? "", frist: anzeige?.frist ?? "",
    status: anzeige?.status ?? "offen", antwort: anzeige?.antwort ?? "",
  });
  const fertig = (d: Anzeige[]) => { qc.setQueryData(["anzeigen", projekt.id], d); qc.invalidateQueries({ queryKey: ["fristen"] }); };
  const speichern = useMutation({
    mutationFn: () => {
      const rumpf = { ...f, leistung_id: f.leistung_id || null, frist: f.frist || null };
      return anzeige ? api<Anzeige[]>("PATCH", `/anzeigen/${anzeige.id}`, rumpf)
                     : api<Anzeige[]>("POST", `/projekte/${projekt.id}/anzeigen`, { ...rumpf, art });
    },
    onSuccess: (d) => { fertig(d); zu(); },
  });
  const insProtokoll = useMutation({
    mutationFn: () => api<Anzeige[]>("POST", `/anzeigen/${anzeige!.id}/punkt`),
    onSuccess: (d) => { fertig(d); qc.invalidateQueries({ queryKey: ["besprechung"] }); zu(); },
  });
  const loeschen = useMutation({
    mutationFn: () => api<Anzeige[]>("DELETE", `/anzeigen/${anzeige!.id}`),
    onSuccess: (d) => { fertig(d); zu(); },
  });
  const titel = anzeige ? `${ANZEIGE_ART[art]} Nr. ${anzeige.lfd}` : `${ANZEIGE_ART[art]} erfassen`;
  return (
    <Dialog offen zu={zu} titel={titel} fuss={<>
      {anzeige && kann && projekt.meine_rolle === "leitung" && (
        <button className="knopf leise gefahr" style={{ marginRight: "auto" }}
                onClick={() => confirm("Diese Anzeige löschen?") && loeschen.mutate()}><Trash size={15} /> Löschen</button>
      )}
      {anzeige && kann && !anzeige.punkt_id && (
        <button className="knopf leise" disabled={insProtokoll.isPending} onClick={() => insProtokoll.mutate()}>
          <ChatsCircle size={15} /> Ins Protokoll</button>
      )}
      <button className="knopf leise" onClick={zu}>{kann ? "Abbrechen" : "Schließen"}</button>
      {kann && <button className="knopf haupt" disabled={!f.gegenstand.trim() || speichern.isPending}
                       onClick={() => speichern.mutate()}>Speichern</button>}
    </>}>
      <p className="mute" style={{ marginTop: 0 }}>{ANZEIGE_GRUND[art]} – schriftlich eingegangen beim Auftraggeber bzw. der Objektüberwachung.</p>
      <fieldset disabled={!kann} className="raster" style={{ border: 0, padding: 0, margin: 0 }}>
        <Feld titel="Von (Auftragnehmer)"><input className="eingabe" value={f.von} onChange={(e) => setF({ ...f, von: e.target.value })} /></Feld>
        <Feld titel="Eingegangen am"><input type="date" className="eingabe" value={f.eingang_am} onChange={(e) => setF({ ...f, eingang_am: e.target.value })} /></Feld>
        <Feld titel="Leistung"><LeistungWahl leistungen={leistungen} wert={f.leistung_id} setze={(v) => setF({ ...f, leistung_id: v })} /></Feld>
        <Feld titel="Antwort bis"><input type="date" className="eingabe" value={f.frist} onChange={(e) => setF({ ...f, frist: e.target.value })} /></Feld>
        <div className="feld breit">
          <div className="feld-kopf"><span>{art === "behinderung" ? "Behinderung" : "Bedenken gegen"}</span>
            <Diktat text={f.gegenstand} speichern={(v) => setF((x) => ({ ...x, gegenstand: v }))} /></div>
          <textarea className="eingabe" rows={3} aria-label="Gegenstand" value={f.gegenstand}
                    onChange={(e) => setF({ ...f, gegenstand: e.target.value })} />
        </div>
        <Feld titel={art === "behinderung" ? "Angezeigte Auswirkung (Dauer, Kosten)" : "Vorschlag des Auftragnehmers"} breit>
          <textarea className="eingabe" rows={2} value={f.auswirkung} onChange={(e) => setF({ ...f, auswirkung: e.target.value })} />
        </Feld>
        {anzeige && <>
          <Feld titel="Stand">
            <Auswahl value={f.status} onChange={(e) => setF({ ...f, status: e.target.value as AnzeigeStatus })}>
              {Object.entries(ANZEIGE_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </Auswahl>
          </Feld>
          <div className="mute" style={{ alignSelf: "end" }}>{anzeige.beantwortet_am && `beantwortet ${datum(anzeige.beantwortet_am)}`}</div>
          <Feld titel="Antwort / Ergebnis" breit>
            <textarea className="eingabe" rows={2} value={f.antwort} onChange={(e) => setF({ ...f, antwort: e.target.value })} />
          </Feld>
        </>}
      </fieldset>
      <Meldung fehler={speichern.error || insProtokoll.error || loeschen.error} />
    </Dialog>
  );
}

// ================================================================ Nachträge

function NachtragListe({ projekt, kann, leistungen }: { projekt: Projekt; kann: boolean; leistungen: Leistung[] }) {
  const liste = useQuery({ queryKey: ["nachtraege", projekt.id], queryFn: () => lies<Nachtraege>(`/projekte/${projekt.id}/nachtraege`) });
  const [neu, setNeu] = useState(false);
  const [offen, setOffen] = useState<Nachtrag | null>(null);
  const s = liste.data?.summe;
  return (
    <section className="karte">
      <div className="karte-kopf">
        <h2>Nachträge</h2>
        {s && <span className="mute">beauftragt {euro(s.beauftragt) || "0,00 €"} · in Prüfung {euro(Number(s.angeboten) + Number(s.geprueft))}</span>}
        <div className="luft" />
        {kann && <button className="knopf" onClick={() => setNeu(true)}><Plus size={16} weight="bold" /> Nachtrag</button>}
      </div>
      <Meldung fehler={liste.error} />
      <div className="liste">
        {liste.data?.nachtraege.map((x) => (
          <button key={x.id} className="zeile zeile-knopf" onClick={() => setOffen(x)}>
            <span className="nr">{x.nummer}</span>
            <div className="haupt">
              <div>{x.gegenstand}</div>
              <div className="mute">{[x.leistung, x.angebot_am && `Angebot ${datum(x.angebot_am)}`,
                                       x.beauftragt_am && `beauftragt ${datum(x.beauftragt_am)}`].filter(Boolean).join(" · ")}</div>
            </div>
            <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span className="ziffern">{euro(x.betrag_beauftragt ?? x.betrag_geprueft ?? x.betrag_angebot)}</span>
              <span className={`chip ${FARBE[x.status]}`}>{NACHTRAG_STATUS[x.status]}</span>
            </span>
          </button>
        ))}
        {liste.data?.nachtraege.length === 0 && <div className="leer">Keine Nachträge.</div>}
      </div>
      {neu && <NachtragDialog projekt={projekt} leistungen={leistungen} kann zu={() => setNeu(false)} />}
      {offen && <NachtragDialog projekt={projekt} nachtrag={offen} leistungen={leistungen} kann={kann} zu={() => setOffen(null)} />}
    </section>
  );
}

function NachtragDialog({ projekt, nachtrag, leistungen, kann, zu }: {
  projekt: Projekt; nachtrag?: Nachtrag; leistungen: Leistung[]; kann: boolean; zu: () => void;
}) {
  const qc = useQueryClient();
  const zahl = (w: number | string | null | undefined) => (w === null || w === undefined ? "" : String(w));
  const [f, setF] = useState({
    nummer: nachtrag?.nummer ?? "", leistung_id: nachtrag?.leistung_id ?? "", gegenstand: nachtrag?.gegenstand ?? "",
    status: nachtrag?.status ?? "angekuendigt", angebot_am: nachtrag?.angebot_am ?? "",
    betrag_angebot: zahl(nachtrag?.betrag_angebot), betrag_geprueft: zahl(nachtrag?.betrag_geprueft),
    betrag_beauftragt: zahl(nachtrag?.betrag_beauftragt), bemerkung: nachtrag?.bemerkung ?? "",
  });
  const geld = (v: string) => (v.trim() ? v.replace(/\./g, "").replace(",", ".") : null);
  const fertig = (d: Nachtraege) => qc.setQueryData(["nachtraege", projekt.id], d);
  const speichern = useMutation({
    mutationFn: () => {
      const rumpf = {
        ...f, leistung_id: f.leistung_id || null, angebot_am: f.angebot_am || null,
        betrag_angebot: geld(f.betrag_angebot), betrag_geprueft: geld(f.betrag_geprueft), betrag_beauftragt: geld(f.betrag_beauftragt),
      };
      if (nachtrag) return api<Nachtraege>("PATCH", `/nachtraege/${nachtrag.id}`, rumpf);
      const { betrag_geprueft: _g, betrag_beauftragt: _b, bemerkung: _m, ...neu } = rumpf;
      return api<Nachtraege>("POST", `/projekte/${projekt.id}/nachtraege`, neu);
    },
    onSuccess: (d) => { fertig(d); zu(); },
  });
  const insProtokoll = useMutation({
    mutationFn: () => api<Nachtraege>("POST", `/nachtraege/${nachtrag!.id}/punkt`),
    onSuccess: (d) => { fertig(d); qc.invalidateQueries({ queryKey: ["besprechung"] }); zu(); },
  });
  const loeschen = useMutation({
    mutationFn: () => api<Nachtraege>("DELETE", `/nachtraege/${nachtrag!.id}`),
    onSuccess: (d) => { fertig(d); zu(); },
  });
  return (
    <Dialog offen zu={zu} titel={nachtrag ? `Nachtrag ${nachtrag.nummer}` : "Nachtrag erfassen"} fuss={<>
      {nachtrag && kann && projekt.meine_rolle === "leitung" && (
        <button className="knopf leise gefahr" style={{ marginRight: "auto" }}
                onClick={() => confirm("Diesen Nachtrag löschen?") && loeschen.mutate()}><Trash size={15} /> Löschen</button>
      )}
      {nachtrag && kann && !nachtrag.punkt_id && (
        <button className="knopf leise" disabled={insProtokoll.isPending} onClick={() => insProtokoll.mutate()}>
          <ChatsCircle size={15} /> Ins Protokoll</button>
      )}
      <button className="knopf leise" onClick={zu}>{kann ? "Abbrechen" : "Schließen"}</button>
      {kann && <button className="knopf haupt" disabled={!f.gegenstand.trim() || speichern.isPending}
                       onClick={() => speichern.mutate()}>Speichern</button>}
    </>}>
      <fieldset disabled={!kann} className="raster" style={{ border: 0, padding: 0, margin: 0 }}>
        <Feld titel="Nummer"><input className="eingabe" placeholder="automatisch (NA 01 …)" value={f.nummer}
                                    onChange={(e) => setF({ ...f, nummer: e.target.value })} /></Feld>
        <Feld titel="Stand">
          <Auswahl value={f.status} onChange={(e) => setF({ ...f, status: e.target.value as NachtragStatus })}>
            {Object.entries(NACHTRAG_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Auswahl>
        </Feld>
        <Feld titel="Gegenstand" breit>
          <textarea className="eingabe" rows={2} value={f.gegenstand} onChange={(e) => setF({ ...f, gegenstand: e.target.value })} />
        </Feld>
        <Feld titel="Leistung"><LeistungWahl leistungen={leistungen} wert={f.leistung_id} setze={(v) => setF({ ...f, leistung_id: v })} /></Feld>
        <Feld titel="Angebot vom"><input type="date" className="eingabe" value={f.angebot_am} onChange={(e) => setF({ ...f, angebot_am: e.target.value })} /></Feld>
        <Feld titel="Angebot (netto, €)"><input className="eingabe ziffern" inputMode="decimal" value={f.betrag_angebot}
                                                 onChange={(e) => setF({ ...f, betrag_angebot: e.target.value })} /></Feld>
        {nachtrag && <>
          <Feld titel="Geprüft (netto, €)"><input className="eingabe ziffern" inputMode="decimal" value={f.betrag_geprueft}
                                                   onChange={(e) => setF({ ...f, betrag_geprueft: e.target.value })} /></Feld>
          <Feld titel="Beauftragt (netto, €)"><input className="eingabe ziffern" inputMode="decimal" value={f.betrag_beauftragt}
                                                      onChange={(e) => setF({ ...f, betrag_beauftragt: e.target.value })} /></Feld>
          <div />
          <Feld titel="Bemerkung" breit>
            <textarea className="eingabe" rows={2} value={f.bemerkung} onChange={(e) => setF({ ...f, bemerkung: e.target.value })} />
          </Feld>
        </>}
      </fieldset>
      <Meldung fehler={speichern.error || insProtokoll.error || loeschen.error} />
    </Dialog>
  );
}
